import type {
  TemplateScheduleWindow,
} from 'src/store/generated/leela-api-generated';

export interface ComputedSchedule {
  startTime: string;
  endTime: string;
}

function getUtcOffsetMs (date: Date, timezone: string): number {
  const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
  return new Date(utcStr).getTime() - new Date(tzStr).getTime();
}

function localTimeToUtc (year: number, month: number, day: number, hour: number, minute: number, timezone: string): Date {
  const approx = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const offsetMs1 = getUtcOffsetMs(approx, timezone);
  const firstPass = new Date(Date.UTC(year, month - 1, day, hour, minute) + offsetMs1);
  const offsetMs2 = getUtcOffsetMs(firstPass, timezone);
  return new Date(Date.UTC(year, month - 1, day, hour, minute) + offsetMs2);
}

function getEventLocalDate (predictedStartTimeUTC: string, timezone: string): { year: number; month: number; day: number } {
  const date = new Date(predictedStartTimeUTC);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  const [year, month, day] = parts.split('-').map(Number);
  return { year, month, day };
}

function resolveField (
  field: TemplateScheduleWindow['goesLive'],
  predictedStartTime: string,
  predictedEndTime: string | null | undefined,
  timezone: string,
): Date {
  if (field.mode === 'EVENT') {
    const anchorTime = field.anchor === 'EVENT_END'
      ? new Date(predictedEndTime ?? predictedStartTime)
      : new Date(predictedStartTime);

    const offsetMs = field.unit === 'DAYS'
      ? field.offset * 24 * 60 * 60 * 1000
      : field.offset * 60 * 60 * 1000;

    return field.direction === 'BEFORE'
      ? new Date(anchorTime.getTime() - offsetMs)
      : new Date(anchorTime.getTime() + offsetMs);
  }

  const eventDay = getEventLocalDate(predictedStartTime, timezone);
  const targetDate = new Date(Date.UTC(eventDay.year, eventDay.month - 1, eventDay.day - field.daysBeforeEvent));
  const [h, m] = field.timeOfDay.split(':').map(Number) as [number, number, number];
  return localTimeToUtc(targetDate.getUTCFullYear(), targetDate.getUTCMonth() + 1, targetDate.getUTCDate(), h, m, timezone);
}

export function computeAssetSchedule (
  scheduleWindow: TemplateScheduleWindow,
  predictedStartTime: string | null | undefined,
  publisherTimezone: string,
  predictedEndTime?: string | null,
): ComputedSchedule | undefined {
  if (!predictedStartTime) return undefined;
  if (!scheduleWindow.goesLive || !scheduleWindow.takenDown) return undefined;

  const startTime = resolveField(scheduleWindow.goesLive, predictedStartTime, predictedEndTime, publisherTimezone);
  const endTime = resolveField(scheduleWindow.takenDown, predictedStartTime, predictedEndTime, publisherTimezone);

  return {
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  };
}
